import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Ticket, CartProduct } from '@app/modules/model/ticket';
import { TicketProduct } from '@app/modules/model/ticketproduct';

import { languageSupport, languageService } from '@app/modules/common/language'
import { viewService } from '@app/modules/view';

@Component({
    selector: 'ui-ticket-product',
    templateUrl:'./ui-ticket.html',
    styleUrls: [ 
        './ui-ticket.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketProduct extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('ticket') _ticket: Ticket = null;
    @Input('product') _product: CartProduct = null;
    @Input('expand') _expand: boolean = false;

    RequestedTrack(index: number, requested: any): any {
        return requested.refid;
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get IsSeparator(){
        return (this._product.status == 'SP');
    }

    private get TotalKg(){
        if (this._product.product.IsWeighted){
            return (this._product.requested[0].weight / 1000);
        }
        return null;
    }

    get Amount(){
        let _amount = this._product.requested.length; 
        if (this._product.product.IsWeighted){
            return (Math.round(_amount * this.TotalKg * 100) / 100).toFixed(2) + this.lang.tr("@kilograms");
        }
        return _amount.toString();
    }

    get ItemPrice(){
        let _price = this._product.requested[0].TotalPrice;

        let _product = this._product.product;
        if (_product.IsWeighted){
            _price = _product.MarketPrice ? this._product.requested[0].market : this._product.product.price;
        }

        return (_price || 0).toFixed(2);
    }
    
    get TotalPrice(){
        return this._product.totalprice.toFixed(2);
    }

    /** Espeja el bypass ExtendOrder de la comanda (ui-change) para
     *  que ui-ticket-modify no diverja de cocina. */
    get _extendOrder(){
        return !!(this._ticket && this._ticket.place && this._ticket.place.ExtendOrder);
    }

    private get _DisplayName(){
        if (this._extendOrder){
            return this._product.product ? this._product.product.name : '';
        }
        let _requested = (this._product.requested.length > 0) ? this._product.requested[0] : null;
        if (_requested){
            return _requested.DisplayName;
        }
        return this._product.product ? this._product.product.name : '';
    }

    /** Label per-row en la lista expandida (split selection). */
    RequestedName(_requested: TicketProduct): string {
        if (!_requested){
            return '';
        }
        if (this._extendOrder){
            return _requested.product ? _requested.product.name : '';
        }
        return _requested.DisplayName;
    }

    get Requested(){    // innerHTML label; sin esto no refresca en safari
        return this.Amount + ' x <b>' +  this._DisplayName  + '</b>'
    }

    // Extras seleccionados en una línea (ej. "avena, sin lactosa"), para ver de un
    // vistazo cómo está modificado un producto sin desplegar. Reusa DisplayOptions
    // (que ya excluye el producto base) y devuelve null si no hay extras.
    get OptionsInline(){
        let _options = this.DisplayOptions;
        if (!_options || (_options.length == 0)){
            return null;
        }
        return _options.map(_option => _option.name).filter(Boolean).join(', ');
    }

    get ProductImg(){
        if (this._extendOrder){
            return this._product.product ? this._product.product.avatar : null;
        }
        let _requested = (this._product.requested.length > 0) ? this._product.requested[0] : null;
        if (_requested){
            return _requested.DisplayImg;
        }
        return this._product.product ? this._product.product.avatar : null;
    }

    /** Options bajo el header. Si hay direct, solo extras; sino todas.
     *  ExtendOrder: todas siempre (igual que cocina). */
    get DisplayOptions(){
        if (this._extendOrder){
            return this._product.options;
        }
        let _requested = (this._product.requested.length > 0) ? this._product.requested[0] : null;
        if (_requested && _requested.ActiveDirect){
            return _requested.ExtraProductOptions;
        }
        return this._product.options;
    }

    get ProductWeight(){
        let _totalkg = this.TotalKg;
        if (_totalkg){
            return _totalkg.toFixed(2) + this.lang.tr("@kilograms");
        }
        return null;
    }

    get ProductPrice(){
        let _price = (this._product.product.MarketPrice) ? this._product.requested[0].market : this._product.product.price;
        if (_price == null){
            return null;
        }
        if (this._product.product.IsWeighted){
            return _price.toFixed(2) + this.lang.tr('@currency') + '/' + this.lang.tr('@kilograms') ;
        }
        return _price.toFixed(2) + this.lang.tr('@currency');
    }

    get IsAvailable() {
        // check that all options are available
        for (let _option of this._product.requested[0].options){
            if (!_option.IsAvailable){
                return false;
            }
        }
        
        // check that product and parents are available
        let _product = this._product.product;
        while(_product != null){
            if (!_product.IsAvailable){
                return false;
            }
            _product = _product.parent;
        }
        
        return true;           
    }

    get DoneStatus(){
        if (!this._ticket.IsDirect) {
            let _predecence = [ 'RD' ];
            for(let _evt of _predecence){   // return the event with greater precedence
                if ((_evt in this._product.events) && (this._product.events[_evt])){
                    return _evt;
                }                
            }    
        }

        return null;
    }

    get PaidStatus(){
        let _predecence = [ 'PD', 'PP' ];
        for(let _evt of _predecence){   // return the event with greater precedence
            if ((_evt in this._product.events) && (this._product.events[_evt])){
                return _evt;
            }                
        }

        return null;
    }

    get HasOffer(){
        return this._product.offer && this._product.offer.IsValid;
    }

    get Repriced(){
        return this.HasOffer || this._product.fixed;
    }

    get HasRefund(){
        for(let requested of this._product.requested){
            if (requested.status == 'UN'){
                return true;
            }
        }
        return false;
    }

    EventColor(type){
        switch(type){
            case 'AC':
            case 'PP': return 'medium';  
            case 'RD':
            case 'PD': return 'success';
        }
        
        return 'dark';
    }

    /* expand / collapse options */

    private _showoptions = false;
    get ShowOptions(){
        return this._showoptions;
    }

    set ShowOptions(value){
        this._showoptions = value;
        this.change.markForCheck();
    }

    OnShowOptions(){
        this.ShowOptions = !this.ShowOptions;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    private _subscriptions = [];

    constructor(private lang: languageService, private change: ChangeDetectorRef, public view: viewService) {
        super(lang, change);
    }

    private _theme_subscription = null;

    ngOnInit(){
        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        while (this._subscriptions.length > 0) {
            let _subscription = this._subscriptions.shift();
            if (_subscription){
                _subscription.unsubscribe();
            }
        } 
    }

    ngOnChanges(){
        if (!this._expand){
            for(let requested of this._product.requested){
                requested['isSelected'] = false;
            }    
        }

        // clear the current subscriptions
        while (this._subscriptions.length > 0) {
            let _subscription = this._subscriptions.shift();
            if (_subscription){
                _subscription.unsubscribe();
            }
        } 

        // subscribe to product changes
        if (this._product.product){
            this._subscriptions.push(
                this._product.product.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            )    
        }

        // subscribe to ticket changes
        this._subscriptions.push(
            this._ticket.OnRefresh.subscribe(
            data => {
                this.OnSelectAny();
            })
        )

        this.change.markForCheck();
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    public _checkall = false;

    OnSelectAll(){
        setTimeout(() => {
            let _checkall = this._checkall;
            for(let requested of this._product.requested){
                requested['isSelected'] = _checkall;
            }

            this.change.markForCheck();     
        }, 0);
    }

    OnSelectAny(){
        setTimeout(() => {
            this._checkall = false;
            for(let requested of this._product.requested){
                this._checkall ||= requested['isSelected'];
            }

            this.change.markForCheck();     
        }, 0);
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();  
    OnShowProduct(){
        if (this._product.product.IsVarious){
            return;     // various product is not editable
        }

        this._onPanel.emit({
            args: {
                product: this._product.product
            }
        });
    }
}
